import { api } from '@/lib/api/client';
import { isIndexSymbol, normalizeSymbol } from '@/lib/utils/symbol';

export interface DescriptionRecord {
  symbol: string;
  name?: string;
  description: string | null;
  status: 'manual' | 'automatic' | 'missing';
  source: { kind?: string; label?: string; note?: string; url?: string; review_required?: boolean; [key: string]: unknown };
  auto_text?: string | null;
  auto_source?: Record<string, unknown>;
  revision: number;
  manual_note?: string | null;
  manual_url?: string | null;
  updated_at?: string | null;
}
export interface Snapshot { state: 'loading' | 'ready' | 'error'; record?: DescriptionRecord }
const loading: Snapshot = { state: 'loading' };
const cache = new Map<string, { value: Snapshot; at: number }>();
const listeners = new Map<string, Set<() => void>>();
const pending = new Set<string>();
let timer: ReturnType<typeof setTimeout> | undefined;
let running = false;
const TTL = 180_000;

export function descriptionSymbol(raw?: string | null) {
  if (!raw || isIndexSymbol(raw)) return '';
  const symbol = normalizeSymbol(raw);
  // Preserve international canonical tickers; never infer an A-share from an index.
  return /^(?:\d{6}\.(?:SH|SZ|BJ)|\d{5}\.HK|[A-Z]{1,6}(?:[.-][A-Z]{1,3})?)$/.test(symbol) ? symbol : '';
}
export const getDescriptionSnapshot = (symbol: string): Snapshot => cache.get(symbol)?.value ?? loading;
function publish(symbol: string, value: Snapshot) {
  cache.set(symbol, { value, at: Date.now() });
  listeners.get(symbol)?.forEach(fn => fn());
  if (cache.size > 8000) {
    for (const key of cache.keys()) {
      if (!listeners.get(key)?.size) cache.delete(key);
      if (cache.size <= 6000) break;
    }
  }
}
async function drain() {
  timer = undefined;
  if (running) return;
  running = true;
  try {
    while (pending.size) {
      const symbols = [...pending].slice(0, 200);
      symbols.forEach(s => pending.delete(s));
      try {
        const { data } = await api.post<{ items: DescriptionRecord[] }>('/stock-descriptions/batch', { symbols }, { baseURL: '/api/v2', timeout: 15_000 });
        const records = new Map(data.items.map(r => [r.symbol, r]));
        symbols.forEach(s => {
          const record = records.get(s);
          publish(s, record ? { state: 'ready', record } : { state: 'error' });
        });
      } catch {
        symbols.forEach(s => publish(s, { state: 'error' }));
      }
    }
  } finally { running = false; }
}
function enqueue(symbol: string) {
  pending.add(symbol);
  if (!timer && !running) timer = setTimeout(() => { void drain(); }, 20);
}
export function subscribeDescription(symbol: string, listener: () => void) {
  if (!symbol) return () => {};
  let set = listeners.get(symbol);
  if (!set) { set = new Set(); listeners.set(symbol, set); }
  set.add(listener);
  const stored = cache.get(symbol);
  if (!stored || Date.now() - stored.at > TTL) enqueue(symbol);
  return () => { set!.delete(listener); if (!set!.size) listeners.delete(symbol); };
}
export function refreshDescription(symbol?: string, record?: DescriptionRecord) {
  if (symbol && record) { publish(symbol, { state: 'ready', record }); return; }
  const symbols = symbol ? [symbol] : [...listeners.keys()];
  if (!symbol) cache.clear();
  symbols.forEach(s => { cache.delete(s); enqueue(s); listeners.get(s)?.forEach(fn => fn()); });
}
